/**
 * roleForDisplay.ts
 * ──────────────────────────────────────────────────────────────────────────
 * SKPLATFORM-UICC-001 CH-U6 — which role the console shows on the badge.
 *
 * ── What the CH-U6 sweep found ────────────────────────────────────────────
 * The main question was whether the console decodes a JWT client-side and
 * reads the deprecated singular `role` claim. **It does not** — there is no
 * `atob`, no `jwt-decode`, no `jose` anywhere in `src/`. Every role the console
 * displays arrives from `GET /v1/me`. So the claim migration (CC-002 CH-2) is
 * "N/A — verified" for this repo.
 *
 * What the sweep DID find is the display rule. The badge read
 * `memberships[0].role` — whichever membership the API happened to return
 * first. For anyone in more than one org that is arbitrary: an owner of one org
 * who is a plain member of another could see either badge, and it could change
 * between reloads with no rule behind it.
 *
 * CH-U6 specifies "the highest-privilege global role as the badge". This
 * implements the stronger version: the CURRENT org's membership wins, because
 * the badge describes the workspace on screen — telling an Owner-of-A that they
 * are an Owner while they are looking at B is exactly where they would then try
 * to act. Highest-privilege is the fallback when nothing matches.
 */

/**
 * Precedence, most privileged first. Mirrors the order documented in
 * skplatform-api's `lib/auth/jwt.service.ts`.
 *
 * Keep them in step: if the console ranks differently from the API, the badge
 * claims one thing while the API enforces another — and people believe badges.
 */
export const ROLE_PRECEDENCE = ['platform_admin', 'org_admin', 'admin', 'user'] as const;

/**
 * The most privileged of `roles`.
 *
 * Roles this console does not know are ranked below every known one rather than
 * dropped: a new role from a newer API should not win by accident, but showing
 * an unfamiliar label beats showing an empty badge — the person can at least
 * read it out to support.
 */
export function highestRole(roles: readonly string[] | null | undefined): string | null {
  if (!Array.isArray(roles) || roles.length === 0) return null;

  const known = roles.filter((r) => typeof r === 'string' && (ROLE_PRECEDENCE as readonly string[]).includes(r));
  if (known.length > 0) {
    for (const candidate of ROLE_PRECEDENCE) {
      if (known.includes(candidate)) return candidate;
    }
  }

  const firstUnknown = roles.find((r) => typeof r === 'string' && r.length > 0);
  return firstUnknown ?? null;
}

interface MeLike {
  org?: { id?: string } | null;
  memberships?: Array<{ org_id?: string; role?: string } | null> | null;
  /** Forward compatibility: honoured if `/v1/me` ever returns it. */
  roles?: string[] | null;
  /** Platform-scoped roles (user_roles with org_id=null). */
  platform_roles?: string[] | null;
  is_platform_admin?: boolean | null;
  /**
   * SOFTKNACK'S OWN PEOPLE — us, not a tenant's employees.
   *
   * Named for the company on purpose. `staff` alone would be wrong here: the
   * token's `realm: 'staff'` already means "a human working for the OPERATOR"
   * (a salon's receptionist included), so a bare `staff` field would read as
   * that and mean the opposite.
   */
  softknack_staff?: { roles?: string[] } | boolean | null;
}

/**
 * Is the signed-in person one of SOFTKNACK's own people (not a tenant's staff)?
 *
 * Gates the Admin Console. Reads the new `softknack_staff` claim first, then
 * today's `is_platform_admin` / `platform_roles`, so this survives the claim
 * migration without a second FE change.
 *
 * Deliberately does NOT look at `roles[]`. Company affiliation is a separate
 * axis from tenant authorization, and carrying `platform_admin` inside `roles[]`
 * is the defect being removed — it outranks `org_admin` there, which is why a
 * Softknack person could never see the normal customer UI.
 */
export function isSoftknackStaff(me: MeLike | null | undefined): boolean {
  if (!me || typeof me !== 'object') return false;
  if (me.softknack_staff === true) return true;
  if (me.softknack_staff && typeof me.softknack_staff === 'object') return true;
  if (me.is_platform_admin === true) return true;
  return Array.isArray(me.platform_roles) && me.platform_roles.includes('platform_admin');
}

/**
 * The role to render for the signed-in person, given a `/v1/me` payload.
 *
 * Total by construction — a malformed membership row must not throw inside the
 * session context provider, because that blanks the entire shell rather than
 * just the badge.
 */
export function roleForDisplay(me: MeLike | null | undefined): string | null {
  if (!me || typeof me !== 'object') return null;

  // Prefer an explicit roles[] if the API grows one, so the badge does not need
  // a second migration when it does.
  const fromRoles = highestRole(me.roles ?? null);
  if (fromRoles) return fromRoles;

  const memberships = Array.isArray(me.memberships) ? me.memberships : [];
  const rows = memberships.filter((m): m is { org_id?: string; role?: string } => !!m && typeof m === 'object');

  const currentOrgId = me.org?.id;
  if (currentOrgId) {
    const mine = rows.find((m) => m.org_id === currentOrgId && typeof m.role === 'string' && m.role);
    if (mine?.role) return mine.role;
  }

  return highestRole(rows.map((m) => m.role).filter((r): r is string => typeof r === 'string' && r.length > 0));
}
